import { useEffect } from 'react';

import { usePlanEditor } from './editor';

/**
 * A click on empty space in Planning — anywhere that is not a task — clears
 * the active task, so the right panel goes back to the project overview
 * (Mette, 3 Nov). What does not count as outside:
 *
 * - a task: a list row, a timeline row or its bar (those make themselves active);
 * - any control — a button, link, field or menu item does its own thing, and
 *   some (Add task) make a task active, which a clear right after would undo;
 * - the side panel, whose task insight is what the active task is for;
 * - anything outside the page, i.e. a menu, popover or dialog in a portal.
 *
 * Selection (the checkboxes) is left alone.
 */
const TASK =
  '[data-automation-id="plan-row"], [data-automation-id="timeline-row"], [data-automation-id="timeline-bar"]';
const CONTROL =
  'button, a, input, textarea, select, label, [role="button"], [role="menuitem"], [role="option"], [role="checkbox"], [contenteditable="true"]';
const KEEPS_ACTIVE = `${TASK}, ${CONTROL}, [data-automation-id="panels-drawer"], [data-automation-id="panels-resize-handle"]`;

export const useDeselectOnOutsideClick = () => {
  const { state, dispatch } = usePlanEditor();
  const active = state.activeId !== null;
  useEffect(() => {
    if (!active) return undefined;
    const onClick = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (!target.closest('[data-automation-id="planning-page"]')) return;
      if (target.closest(KEEPS_ACTIVE)) return;
      // A drag-select that ends on empty space is not a click on it.
      if (window.getSelection()?.toString()) return;
      dispatch({ type: 'SET_ACTIVE', id: null });
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, [active, dispatch]);
};
